import { cn } from '@/lib/utils';

// The one switch.
//
// This existed twice — `routes/inboxes.tsx` and `routes/automations.tsx` — with
// the same 20×36 track, the same 16px thumb, and the same magic
// `translate-x-[18px]` offset. The second copy's comment said the quiet part
// out loud: "same shape as inboxes.tsx — avoids pulling a Switch primitive".
//
// Both copies also shared an accessibility bug: `label` had no `htmlFor` in
// practice (the `id` prop was optional and the only caller never passed it),
// and the label's sole child was `aria-hidden`, so every toggle was an
// anonymous checkbox. `label` is now required, which makes it impossible to
// render one of these without a name.

export interface SwitchProps {
  checked: boolean;
  onCheckedChange: (next: boolean) => void;
  disabled?: boolean;
  /** Accessible name. Required — an unnamed toggle is unusable non-visually. */
  label: string;
  /** Set when a visible label already names the control, to avoid announcing
   *  it twice. The `label` prop is still used as the aria-label fallback. */
  labelledBy?: string;
  /** Longer explanation, associated via aria-describedby. */
  describedBy?: string;
  /** Tone of the "on" state. `success` for "this is running / active", the
   *  neutral default for ordinary preferences. */
  tone?: 'default' | 'success';
  className?: string;
}

export function Switch({
  checked,
  onCheckedChange,
  disabled,
  label,
  labelledBy,
  describedBy,
  tone = 'default',
  className,
}: SwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={labelledBy ? undefined : label}
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      disabled={disabled}
      onClick={() => !disabled && onCheckedChange(!checked)}
      className={cn(
        'relative inline-flex h-6 w-10 shrink-0 items-center rounded-full transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        checked
          ? tone === 'success'
            ? 'bg-tint-success-fg'
            : 'bg-foreground'
          : 'bg-border',
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        className,
      )}
    >
      <span
        aria-hidden
        className={cn(
          'inline-block h-5 w-5 transform rounded-full bg-background shadow transition-transform',
          // 40px track − 20px thumb − 2px inset = 18px of travel.
          checked ? 'translate-x-[18px]' : 'translate-x-0.5',
        )}
      />
    </button>
  );
}
